/**
 * Área de administração (docs/spec/admin.md): só para administradores do servidor. As seções ficam à
 * esquerda, como nas Configurações (no celular, numa faixa que rola de lado): Visão geral, Usuários,
 * Planos, Mesas, Plugins oficiais, Servidor e Registro. Os detalhes de uma conta abrem à direita da
 * seção que os pediu.
 */
import { useState, type ReactNode } from 'react';
import { useT } from '../i18n/index.js';
import { ADMIN_PAGES, type AdminPage } from './adminModel.js';
import { AdminLog } from './AdminLog.js';
import { AdminPlanHost } from './AdminPlanDialog.js';
import { AdminOverview } from './AdminOverview.js';
import { AdminPlans } from './AdminPlans.js';
import { AdminPlugins } from './AdminPlugins.js';
import { AdminRooms } from './AdminRooms.js';
import { AdminServer } from './AdminServer.js';
import type { AdminPlanFilter, AdminUserFilter } from '../shared.js';
import { AdminUserPanel } from './AdminUserPanel.js';
import { AdminUsers } from './AdminUsers.js';
import './admin.css';
import { PageHeader } from '../ui/PageHeader.js';

/** As seções em que os detalhes de uma conta abrem ao lado; nas outras, abrir uma conta leva a Usuários. */
const WITH_PANEL: ReadonlySet<AdminPage> = new Set(['users', 'log', 'rooms']);

export function AdminState({ initialPage = 'overview' }: { initialPage?: AdminPage | undefined }) {
  const t = useT();
  const [page, setPage] = useState<AdminPage>(initialPage);
  // a conta aberta no painel de detalhes
  const [selected, setSelected] = useState<string | null>(null);
  // o filtro com que Usuários abre, quando vem de um número da Visão geral
  const [usersPreset, setUsersPreset] = useState<{ filter: AdminUserFilter; plan: AdminPlanFilter }>({ filter: 'all', plan: 'all' });
  // muda a cada ação: as listas, o painel e o registro recarregam
  const [version, setVersion] = useState(0);
  // o lugar das ações da seção no cabeçalho: a seção as põe lá ("Adicionar plugin…")
  const [headActions, setHeadActions] = useState<HTMLDivElement | null>(null);
  const changed = () => setVersion((v) => v + 1);
  /** Abre os detalhes de uma conta, a partir de qualquer seção. */
  const openUser = (userId: string) => {
    if (!WITH_PANEL.has(page)) setPage('users');
    setSelected(userId);
  };
  const go = (next: AdminPage) => {
    setPage(next);
    if (!WITH_PANEL.has(next)) setSelected(null);
    if (next === 'users') setUsersPreset({ filter: 'all', plan: 'all' });
  };
  /** Abre Usuários com um filtro (os números da Visão geral). */
  const openUsers = (preset: Partial<{ filter: AdminUserFilter; plan: AdminPlanFilter }>) => {
    setUsersPreset({ filter: preset.filter ?? 'all', plan: preset.plan ?? 'all' });
    setPage('users');
  };

  let body: ReactNode;
  if (page === 'users')
    body = (
      <AdminUsers
        key={`${usersPreset.filter}:${usersPreset.plan}`}
        selected={selected}
        onSelect={setSelected}
        version={version}
        onChanged={changed}
        initialFilter={usersPreset.filter}
        initialPlan={usersPreset.plan}
      />
    );
  else if (page === 'overview') body = <AdminOverview version={version} onOpenUsers={openUsers} onOpenRooms={() => go('rooms')} />;
  else if (page === 'log') body = <AdminLog version={version} onOpenUser={openUser} />;
  else if (page === 'plans') body = <AdminPlans version={version} onChanged={changed} />;
  else if (page === 'rooms') body = <AdminRooms version={version} onChanged={changed} onOpenUser={openUser} />;
  else if (page === 'plugins') body = <AdminPlugins version={version} onChanged={changed} actionsSlot={headActions} />;
  else body = <AdminServer version={version} />;

  return (
    <div className="rd-adminShell">
      <nav className="rd-adminShell__nav" aria-label={t('admin.title')}>
        <h2 className="rd-navGroupLabel rd-adminShell__navTitle">{t('admin.title')}</h2>
        {ADMIN_PAGES.map((p) => (
          <button key={p} type="button" className={`rd-navItem rd-adminShell__navItem${page === p ? ' is-on' : ''}`} aria-current={page === p ? 'page' : undefined} onClick={() => go(p)}>
            {t(`admin.section.${p}`)}
          </button>
        ))}
      </nav>
      {/* a região com o nome é a de cada seção */}
      <div className="rd-admin">
        <PageHeader className="rd-admin__head" level={2} title={t(`admin.section.${page}`)} actions={<div className="rd-admin__headActions" ref={setHeadActions} />} />
        <div className={`rd-admin__body${selected && WITH_PANEL.has(page) ? ' has-panel' : ''}`}>
          {body}
          {selected && WITH_PANEL.has(page) ? <AdminUserPanel key={selected} userId={selected} version={version} onChanged={changed} onClose={() => setSelected(null)} /> : null}
        </div>
      </div>
      <AdminPlanHost />
    </div>
  );
}
